import {
  IconArrowDown,
  IconArrowRight,
  IconArrowsSort,
  IconArrowUp,
} from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import {
  BetaLabel,
  formatContent,
  Loading,
  Pagination,
  RankCell,
  ScalrEmptyBox,
  ScalrTableColumn,
  ScalrTableContentProps,
  ScalrTableData,
  ScalrTableProps,
  TrendLabel,
} from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const TableContent = ({
  data,
  columns,
  loading,
  title,
  viewMoreLink,
  showBadge,
  height,
  truncate,
  enablePagination = false,
  itemsPerPage = 4,
  sortBy,
  sortOrder,
  country,
  onHeaderClick,
}: ScalrTableContentProps & {
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onHeaderClick?: (column: ScalrTableColumn) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const [currentPage, setCurrentPage] = useState(1)

  const { paginatedData, totalPages } = useMemo(() => {
    if (!enablePagination) {
      return { paginatedData: data, totalPages: 1 }
    }

    const total = Math.ceil(data.length / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginated = data.slice(startIndex, endIndex)

    return { paginatedData: paginated, totalPages: total }
  }, [data, currentPage, enablePagination, itemsPerPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const renderCell = (
    item: ScalrTableData,
    column: ScalrTableColumn,
    columnIndex: number
  ): JSX.Element => {
    const value = item[column.accessorKey]
    const prevKey = `prev${column.accessorKey.charAt(0).toUpperCase()}${column.accessorKey.slice(1)}`
    const prevValue = item[prevKey]

    // Check if this is the second column (index 0 after filtering out 'rank')
    const isSecondColumn =
      columns.findIndex((c) => c.accessorKey !== 'rank') === 0 &&
      column.accessorKey ===
        columns.find((c) => c.accessorKey !== 'rank')?.accessorKey

    return (
      <TableCell
        key={column.accessorKey}
        className={`h-[78px] ${columnIndex === 0 ? 'w-48 min-w-48' : ''}`}
      >
        <div className="flex">
          {isSecondColumn && item.id ? (
            <Link
              to={returnItemLink(item.id)}
              className={`hover:text-blue-500 ${truncate ? 'truncate max-w-[5rem] hover:max-w-none hover:whitespace-normal' : ''}`}
              title={String(value)}
            >
              {formatContent({ content: value, type: column.type, country })}
            </Link>
          ) : (
            formatContent({
              content: value,
              type: column.type,
              truncate,
              country,
            })
          )}
          {typeof value === 'number' &&
            !column.accessorKey.startsWith('prev') &&
            prevValue !== undefined && (
              <TrendLabel
                oldValue={Number(prevValue)}
                newValue={value}
                isNormal={true}
                className="ml-2"
                trendType={
                  column.type as 'money' | 'percentage' | 'number' | 'hour'
                }
              />
            )}
        </div>
      </TableCell>
    )
  }

  return (
    <div className={`${viewMoreLink ? 'pt-0' : 'pt-2'}`}>
      <div className="flex justify-between items-center p-4">
        <span className="leading-none tracking-tight text-black-500 text-display-s">
          {title}
        </span>

        {viewMoreLink && (
          <Button size="sm" variant="outline" onClick={() => viewMoreLink}>
            View More <IconArrowRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>
      {loading ? (
        <Loading isContainer />
      ) : !data || data.length === 0 ? (
        <ScalrEmptyBox />
      ) : (
        <>
          <div
            style={
              height
                ? {
                    maxHeight: `${height}px`,
                    overflowY: 'auto',
                  }
                : undefined
            }
          >
            <Table>
              <TableHeader className="border-t">
                <TableRow>
                  <TableHead className="text-center">
                    {t('table.rank')}
                  </TableHead>
                  {columns
                    .filter((column) => column.accessorKey !== 'rank')
                    .map((column, index) => (
                      <TableHead
                        key={column.accessorKey}
                        className={`${index === 0 ? 'w-48 min-w-48' : ''} ${
                          column.isSortable
                            ? 'cursor-pointer select-none hover:bg-gray-50'
                            : ''
                        }`}
                        onClick={() => onHeaderClick?.(column)}
                      >
                        <div className="flex items-center gap-2">
                          {column.header}
                          {column.isSortable &&
                            (sortBy === column.accessorKey ? (
                              sortOrder === 'asc' ? (
                                <IconArrowUp className="h-4 w-4" />
                              ) : (
                                <IconArrowDown className="h-4 w-4" />
                              )
                            ) : (
                              <IconArrowsSort className="h-4 w-4 text-gray-400" />
                            ))}
                        </div>
                      </TableHead>
                    ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((item, index) => {
                  const actualIndex = enablePagination
                    ? (currentPage - 1) * itemsPerPage + index
                    : index
                  return (
                    <TableRow key={actualIndex}>
                      <TableCell>
                        <RankCell index={actualIndex} showBadge={showBadge} />
                      </TableCell>
                      {columns
                        .filter((column) => column.accessorKey !== 'rank')
                        .map((column, columnIndex) =>
                          renderCell(item, column, columnIndex)
                        )}
                    </TableRow>
                  )
                })}
                {enablePagination &&
                  Array.from({
                    length: itemsPerPage - paginatedData.length,
                  }).map((_, index) => (
                    <TableRow key={`empty-${index}`}>
                      <TableCell className="h-[78px]">
                        <div className="h-full" />
                      </TableCell>
                      {columns
                        .filter((column) => column.accessorKey !== 'rank')
                        .map((column, columnIndex) => (
                          <TableCell
                            key={column.accessorKey}
                            className={`h-[78px] ${columnIndex === 0 ? 'w-48 min-w-48' : ''}`}
                          >
                            <div className="h-full" />
                          </TableCell>
                        ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

          {enablePagination && totalPages > 1 && (
            <>
              <Separator />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

export const Leaderboard = ({
  data,
  title,
  columns,
  loading = false,
  viewMoreLink,
  sortBy: propSortBy,
  sortOrder: propSortOrder,
  showCard = true,
  showBadge = true,
  height,
  truncate = false,
  enablePagination = false,
  itemsPerPage = 4,
  isBeta = false,
  country = 'US',
}: ScalrTableProps): JSX.Element => {
  // Local state for sorting
  const [sortBy, setSortBy] = useState<string | undefined>(propSortBy)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    propSortOrder ?? 'desc'
  )

  // Sync props to state when they change
  useEffect(() => {
    if (propSortBy !== undefined) {
      setSortBy(propSortBy)
    }
    if (propSortOrder !== undefined) {
      setSortOrder(propSortOrder)
    }
  }, [propSortBy, propSortOrder])

  // Handle column header click
  const handleHeaderClick = (column: ScalrTableColumn) => {
    if (!column.isSortable) return

    if (sortBy === column.accessorKey) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to descending
      setSortBy(column.accessorKey)
      setSortOrder('desc')
    }
  }

  const sortedData = useMemo(() => {
    if (loading || !data) return []
    if (!sortBy) return data

    return [...data].sort((a, b) => {
      const aValue = a[sortBy]
      const bValue = b[sortBy]

      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue
      }

      // Handle strings
      const aStr = String(aValue ?? '')
      const bStr = String(bValue ?? '')
      return sortOrder === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr)
    })
  }, [data, sortBy, loading, sortOrder])

  const content = (
    <TableContent
      data={sortedData}
      columns={columns}
      loading={loading}
      title={title}
      viewMoreLink={viewMoreLink}
      showBadge={showBadge}
      height={height}
      truncate={truncate}
      enablePagination={enablePagination}
      itemsPerPage={itemsPerPage}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onHeaderClick={handleHeaderClick}
      country={country}
    />
  )

  if (!showCard) {
    return content
  }

  return (
    <Card>
      <CardContent className="px-0 pb-0">{content}</CardContent>
      {isBeta && (
        <CardFooter className="flex justify-end items-end p-0">
          <BetaLabel />
        </CardFooter>
      )}
    </Card>
  )
}

const returnItemLink = (id: string | number) => {
  const str = String(id).trim() // <- trim leading/trailing whitespace

  if (/^(Frozen|Hot|Iced)\s+/i.test(str)) {
    const pathName = str.replace(/^(Frozen|Hot|Iced)\s+/i, '')
    return `/clarity-lens/sales-lens/${pathName}`
  }

  return `/clarity-lens/sales-lens/${str}`
}
